/**
 * codeOptimization.js
 * Utilities for optimizing code performance and reducing bundle size
 */

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @param {boolean} immediate - Whether to invoke the function on the leading edge instead of trailing
 * @returns {Function} - The debounced function
 */
export const debounce = (func, wait = 300, immediate = false) => {
  let timeout;
  
  return function executedFunction(...args) {
    const context = this;
    
    const later = function() {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    
    const callNow = immediate && !timeout;
    
    clearTimeout(timeout);
    
    timeout = setTimeout(later, wait);
    
    if (callNow) func.apply(context, args);
  };
};

/**
 * Creates a throttled function that only invokes func at most once per every wait milliseconds.
 * @param {Function} func - The function to throttle
 * @param {number} wait - The number of milliseconds to throttle invocations to
 * @returns {Function} - The throttled function
 */
export const throttle = (func, wait = 300) => {
  let inThrottle;
  let lastFunc;
  let lastTime;
  
  return function() {
    const context = this;
    const args = arguments;
    
    if (!inThrottle) {
      func.apply(context, args);
      lastTime = Date.now();
      inThrottle = true;
    } else {
      clearTimeout(lastFunc);
      
      lastFunc = setTimeout(function() {
        if (Date.now() - lastTime >= wait) {
          func.apply(context, args);
          lastTime = Date.now();
        }
      }, Math.max(wait - (Date.now() - lastTime), 0));
    }
  };
};

/**
 * Creates a memoized function that caches the results of func based on the arguments provided.
 * @param {Function} func - The function to memoize
 * @param {Function} resolver - A function that determines the cache key for storing the result
 * @returns {Function} - The memoized function
 */
export const memoize = (func, resolver) => {
  const memoized = function(...args) {
    const key = resolver ? resolver.apply(this, args) : JSON.stringify(args);
    const cache = memoized.cache;
    
    if (cache.has(key)) {
      return cache.get(key);
    }
    
    const result = func.apply(this, args);
    memoized.cache = cache.set(key, result);
    return result;
  };
  
  memoized.cache = new Map();
  return memoized;
};

/**
 * Removes unused properties from an object to reduce memory usage
 * @param {Object} obj - The object to clean
 * @param {Array<string>} keysToKeep - Array of keys to keep in the object
 * @returns {Object} - A new object with only the specified keys
 */
export const removeUnusedProperties = (obj, keysToKeep) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const result = {};
  
  keysToKeep.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = obj[key];
    }
  });
  
  return result;
};

/**
 * Checks if a variable is unused (null or undefined)
 * @param {any} variable - The variable to check
 * @returns {boolean} - Whether the variable is unused
 */
export const isUnused = (variable) => {
  return variable === null || variable === undefined;
};

/**
 * Provides a default value for a variable if it's unused
 * @param {any} variable - The variable to check
 * @param {any} defaultValue - The default value to use if variable is unused
 * @returns {any} - Either the original variable or the default value
 */
export const withDefault = (variable, defaultValue) => {
  return isUnused(variable) ? defaultValue : variable;
};

/**
 * Safely accesses nested object properties without throwing errors
 * @param {Object} obj - The object to access
 * @param {string} path - The path to the property, using dot notation
 * @param {any} defaultValue - The default value to return if the path doesn't exist
 * @returns {any} - The value at the path or the default value
 */
export const safeGet = (obj, path, defaultValue = undefined) => {
  if (!obj || !path) return defaultValue;
  
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result === null || result === undefined || typeof result !== 'object') {
      return defaultValue;
    }
    
    result = result[key];
  }
  
  return isUnused(result) ? defaultValue : result;
};

/**
 * Performs a shallow comparison of two objects to determine if they are equal
 * @param {Object} objA - The first object
 * @param {Object} objB - The second object
 * @returns {boolean} - Whether the objects are equal
 */
export const shallowEqual = (objA, objB) => {
  if (objA === objB) return true;
  
  if (!objA || !objB || typeof objA !== 'object' || typeof objB !== 'object') {
    return false;
  }
  
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  
  if (keysA.length !== keysB.length) return false;
  
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(objB, key) || objA[key] !== objB[key]) {
      return false;
    }
  }
  
  return true;
};

/**
 * Creates a function that is restricted to invoking func once.
 * Repeat calls to the function return the value of the first invocation.
 * @param {Function} func - The function to restrict
 * @returns {Function} - The new restricted function
 */
export const once = (func) => {
  let result;
  let called = false;
  
  return function(...args) {
    if (!called) {
      called = true;
      result = func.apply(this, args);
    }
    
    return result;
  };
};

/**
 * Utility to detect and clean up memory leaks in React components
 * @param {Object} component - The React component instance
 * @returns {Function} - A cleanup function to call in componentWillUnmount
 */
export const createMemoryLeakDetector = (component) => {
  const componentName = component.constructor.name;
  const timeoutIds = new Set();
  const intervalIds = new Set();
  const subscriptions = new Set();
  
  // Override setTimeout
  const originalSetTimeout = window.setTimeout;
  window.setTimeout = function(callback, delay, ...args) {
    const id = originalSetTimeout(callback, delay, ...args);
    timeoutIds.add(id);
    return id;
  };
  
  // Override setInterval
  const originalSetInterval = window.setInterval;
  window.setInterval = function(callback, delay, ...args) {
    const id = originalSetInterval(callback, delay, ...args);
    intervalIds.add(id);
    return id;
  };
  
  // Method to register subscriptions
  const registerSubscription = (subscription) => {
    subscriptions.add(subscription);
    return subscription;
  };
  
  // Cleanup function
  return () => {
    // Restore original functions
    window.setTimeout = originalSetTimeout;
    window.setInterval = originalSetInterval;
    
    // Clear timeouts
    timeoutIds.forEach(id => {
      window.clearTimeout(id);
    });
    
    // Clear intervals
    intervalIds.forEach(id => {
      window.clearInterval(id);
    });
    
    // Unsubscribe from subscriptions
    subscriptions.forEach(subscription => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    });
    
    console.log(`Memory leak detector: Cleaned up ${timeoutIds.size} timeouts, ${intervalIds.size} intervals, and ${subscriptions.size} subscriptions for ${componentName}`);
  };
};

/**
 * Default export with all code optimization utilities
 */
export default {
  debounce,
  throttle,
  memoize,
  removeUnusedProperties,
  isUnused,
  withDefault,
  safeGet,
  shallowEqual,
  once,
  createMemoryLeakDetector
};